import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useState, useEffect } from 'react';
import API from './api';
import { usePermissions } from './hooks/usePermissions';
import Login from './pages/Login';
import Home from './pages/Home';
import AdminUsers from './pages/AdminUsers';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import Productions from './pages/Productions';
import Inventory from './pages/Inventory';
import Schedules from './pages/Schedules';
import StickyNotes from './pages/StickyNotes';
import MindMap from './pages/MindMap';
import ElectronicApproval from './pages/ElectronicApproval';
import Email from './pages/Email';
import HRManagement from './pages/HRManagement';
import Notices from './pages/Notices';
import Display from './pages/Display';
import Certs from './pages/Certs';
import Contracts from './pages/Contracts';
import MyPage from './pages/MyPage';
import Settings from './pages/Settings';
import Companies from './pages/Companies';
import Contacts from './pages/Contacts';
import Sales from './pages/Sales';
import SalesActivities from './pages/SalesActivities';
import Storage from './pages/Storage';
import BackupRestore from './pages/BackupRestore';
import AiAssistant from './pages/AiAssistant';
import Assets from './pages/Assets';
import Accounting from './pages/Accounting';
import { ChatProvider } from './context/ChatContext';
import ChatSidebarMenu from './components/ChatSidebarMenu';
import ChatMainWindow from './components/ChatMainWindow';
import AutoLogout from './components/AutoLogout';
import TaskRequestAlert from './components/TaskRequestAlert';
import ScheduleNotifyAlert from './components/ScheduleNotifyAlert';
import HrInfoRequestAlert from './components/HrInfoRequestAlert';
import StorageBackupReminderAlert from './components/StorageBackupReminderAlert';
import StorageShareAlert from './components/StorageShareAlert';
import IncomeTaxTableReminderAlert from './components/IncomeTaxTableReminderAlert';
import LaborContractSignAlert from './components/LaborContractSignAlert';
import ActivityFeedBar from './components/ActivityFeedBar';

// 로그인 안 했으면 로그인 페이지로 보내는 보호막
// 관리자/생산관리자/협력사 같은 "역할" 개념은 없습니다. 최고관리자(superadmin)만 항상
// 모든 메뉴에 접근할 수 있고, 그 외 모든 계정은 예외 없이 계정관리 > 권한설정에서 부여한
// 메뉴별 읽기 권한(canRead)에 따라 접근이 결정됩니다. 계정관리 화면(/admin/users) 자체는
// 권한설정으로 부여할 수 있는 항목이 아니라 최고관리자 전용이므로 superadminOnly로 별도 처리합니다.
// 사내 로컬 네트워크에서 접속했는지 여부 — 데이터베이스 백업/복구처럼 외부에서는 절대 쓸 수
// 없어야 하는 민감한 메뉴에 사용합니다(백엔드 localOnly 미들웨어와 동일한 기준).
//
// 2026-08 수정 — 처음엔 여기서 window.location.hostname==='localhost'로 직접 판단했는데,
// 서버 컴퓨터 앞에서도 HTTPS용 Caddy 리버스프록시를 거쳐 https://ims.ninegro.com 도메인으로
// 접속하는 게 일반적인 사용 방식이 되면서 이 값이 실제 접속 위치와 무관하게 항상 false가
// 되어버렸다. 그래서 GET /api/permissions/me가 내려주는 isLocal(백엔드 req.ip 기준)로 바꿨는데,
// 서버 컴퓨터에서도 그 도메인으로 접속하면 공유기의 NAT 헤어핀 경로를 타면서 req.ip가
// 127.0.0.1이 아니게 되는 경우가 실제로 있어(middleware/localOnly.js 주석 참고) 여전히
// 안 보이는 문제가 있었다. 그래서 isLocal의 판정 기준 자체를 "서버 컴퓨터 자기 자신"에서
// "사내 로컬 네트워크(사설 IP 대역) 전체"로 넓혔다 — 대신 백업/복구 메뉴는 이 완화된 기준과
// 별개로 role==='superadmin'(최고관리자 계정 자체, admin 승격 계정 제외)만 통과하도록
// strictSuperadminOnly로 이중 제한한다(2026-08 요청: "최고관리자만 가능하게, 대신
// 로컬네트워크에서만 가능하도록 제한을 걸자"). usePermissions() 훅을 쓰는 곳(PrivateRoute)은
// 훅의 isLocal을, 별도로 /permissions/me를 직접 조회하는 Sidebar는 그 응답의 isLocal을 각각
// 사용한다.

// 회계관리 — 2026-08 메뉴 개편으로 새로 추가된 최상위 메뉴지만 아직 실제 기능은 없다(추후 요청시
// 구현 예정). 다른 미구현 경로에 쓰던 것과 동일한 안내문구를 보여주는 준비중 화면.
function ComingSoon() {
  return <div style={{ padding:40 }}>🚧 준비 중인 메뉴입니다.</div>;
}

function PrivateRoute({ children, menuKey, superadminOnly, strictSuperadminOnly, localOnly }) {
  const { user, authLoading } = useAuth();
  const { loading, canRead, isLocal } = usePermissions();
  const location = useLocation();
  // 새로 열린 탭이 다른 탭의 세션을 넘겨받을 수 있는지 짧게 확인하는 중(최대 0.4초) — 이 사이에
  // 성급하게 로그인 화면으로 튕기지 않는다(AuthContext.js의 다중 탭 세션 동기화 참고).
  if (authLoading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location }} />;
  // isLocal은 usePermissions()가 /permissions/me를 아직 조회 중이면 기본값 false이므로, 로딩
  // 중에 잠깐 "사내 네트워크에서만..." 안내가 깜빡이지 않도록 loading 동안은 이 검사를 건너뛴다.
  if (localOnly && !loading && !isLocal) {
    return <div style={{ padding:40, color:'red' }}>⛔ 이 메뉴는 사내 네트워크에서 접속했을 때만 사용할 수 있습니다.</div>;
  }
  // strictSuperadminOnly — 백업/복구처럼 role==='admin'(계정관리에서 부여받은 관리자)에게도
  // 절대 열어줄 수 없는 극히 민감한 메뉴 전용(2026-08 추가). superadminOnly와 달리 admin을
  // 함께 통과시키지 않고 role 문자열을 그대로 비교한다 — canRead(menuKey) 권한설정 체계도
  // 거치지 않는다(계정관리 > 권한설정으로 켜고 끌 수 있는 항목이 아니기 때문).
  if (strictSuperadminOnly) {
    return user.role === 'superadmin'
      ? children
      : <div style={{ padding:40, color:'red' }}>⛔ 최고관리자만 접근할 수 있습니다.</div>;
  }
  if (superadminOnly) {
    // role==='admin'(계정관리에서 부여받은 관리자)도 최고관리자와 동일한 권한을 가지므로
    // 함께 통과시킨다(2026-08 추가) — 단, "관리자 권한 부여/회수" 기능 자체는 계정관리(MyPage.js)
    // 안에서 role==='superadmin' 리터럴로 별도로 더 엄격하게 막는다.
    return (user.role === 'superadmin' || user.role === 'admin')
      ? children
      : <div style={{ padding:40, color:'red' }}>⛔ 접근 권한이 없습니다.</div>;
  }
  if (loading) return null; // 권한 로딩 중 잠깐 대기 (오탐으로 튕기는 것 방지)
  return canRead(menuKey) ? children : <div style={{ padding:40, color:'red' }}>⛔ 접근 권한이 없습니다.</div>;
}

// 생산전광판 하위 제품군 선택 상태를 사이드바와 Display.js가 함께 쓰는 localStorage 키/이벤트.
// 사이드바에서 체크박스/제품군 이름을 눌러 바뀐 선택값을 즉시 Display.js에 알려줘야 하므로
// (같은 탭 안에서는 storage 이벤트가 발생하지 않음) 커스텀 이벤트를 함께 쏴준다.
const DISPLAY_GROUPS_KEY = 'ims_display_group_ids';
const readSelectedGroupIds = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(DISPLAY_GROUPS_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
};

// ── 사이드바 개인화(드래그앤드롭 메뉴 재구성) ── 2026-08 신규 ─────────────────────────────
// 메뉴 항목의 "무엇을 보여줄지"(라벨/경로/접근제한)와 "어떻게 배치할지"(순서/그룹)를 분리했다.
// MENU_ITEM_META는 메뉴 키 하나하나의 고정된 정보(바뀌지 않음)이고, DEFAULT_MENU_LAYOUT은
// 관리자가 정한 기본 배치 순서다. 사용자가 ✏️ 메뉴 편집으로 드래그앤드롭하면 그 결과가
// user_sidebar_layout 테이블에 본인 전용으로 저장되고, 다음부터는 그 저장된 배치를 쓴다.
const MENU_ITEM_META = {
  'notices':             { label: '📢 공지사항',    to: '/notices' },
  'schedules':           { label: '📅 일정관리',    to: '/schedules' },
  'hr-management':       { label: '👥 인사관리',    to: '/hr-management' },
  // 자산관리 — 2026-08 신규, 인사관리 바로 아래 위치(회계관리와 동일하게 아직 실제 기능 없는
  // 준비중 placeholder).
  'assets':              { label: '🗃️ 자산관리',    to: '/assets' },
  'accounting':          { label: '💰 회계관리',    to: '/accounting' },
  'tasks':               { label: '📋 업무현황',    to: '/tasks' },
  'e-approval':          { label: '🖊️ 전자결재',   to: '/e-approval' },
  'contracts':           { label: '📄 계약관리',    to: '/contracts' },
  'certs':               { label: '🏅 인증관리',    to: '/certs' },
  'email':               { label: '📧 전자메일',    to: '/email' },
  'sticky-notes':        { label: '🗒️ 포스트잇',    to: '/sticky-notes' },
  // 2026-11 요청: 업무현황 > 포스트잇 아래에 마인드맵(실시간 공동편집) 메뉴 추가
  'mindmap':             { label: '🧠 마인드맵',    to: '/mindmap' },
  'sales-activities':    { label: '🤝 영업현황',    to: '/sales-activities' },
  'company-management':  { label: '🏢 업체관리',    to: '/company-management' },
  'contacts':            { label: '📇 명함관리',  to: '/contacts' },
  'sales':               { label: '🛒 출고관리',    to: '/sales' },
  'productions':         { label: '🏭 생산현황',    to: '/productions' },
  'inventory':           { label: '📦 재고관리',    to: '/inventory' },
  'display':             { label: '📺 생산전광판',  to: '/display' },
  'ai-assistant':        { label: '📊 경영분석', to: '/ai-assistant' },
  'storage':             { label: '💾 스토리지',    to: '/storage' },
  'mypage':              { label: '👤 계정관리',    to: '/mypage' },
  // 환경설정 — 2026-09 신규. 계정관리 바로 아래 위치. 기본 최고관리자/관리자 전용이며
  // 계정관리 > 권한설정에서 개별 계정에도 부여 가능(backend/routes/permissions.js 참고).
  'settings':            { label: '⚙️ 환경설정',    to: '/settings' },
  // 2026-08: 권한설정 체계 밖으로 뺐다 — role==='superadmin' 리터럴 값(관리자 권한을 부여받은
  // admin 계정 제외)만 볼 수 있고, 그마저도 사내 로컬 네트워크에서 접속했을 때만 보인다.
  // 사이드바 필터(아래 allowedKeysSet)에서 strictSuperadminOnly를 canRead 대신 사용한다.
  'backup-restore':      { label: '🗄️ 백업/복구',  to: '/backup-restore', localOnly: true, strictSuperadminOnly: true },
};

// 관리자가 정한 기본 배치 — 기본값은 grp-tasks/grp-sales/grp-productions 3개 그룹으로 시작하지만,
// 2026-08 개편으로 사용자가 편집모드에서 메뉴 두 개를 서로 겹쳐서(overlap) 드롭하면 새 그룹이
// 즉석에서 만들어지고 그룹명도 자유롭게 바꿀 수 있다(아래 mergeIntoNewGroup/renameGroupLabel 참고).
// 따라서 실제 저장되는 레이아웃에는 여기 없는 grp-* id를 가진 그룹도 얼마든지 있을 수 있다.
const DEFAULT_MENU_LAYOUT = [
  { type:'item',  key:'notices' },
  { type:'item',  key:'schedules' },
  { type:'item',  key:'hr-management' },
  { type:'item',  key:'assets' },
  { type:'item',  key:'accounting' },
  { type:'group', id:'grp-tasks',       label:'🗂️ 업무관리', children:['tasks','e-approval','contracts','certs','email','sticky-notes','mindmap'] },
  { type:'group', id:'grp-sales',       label:'💼 영업관리', children:['sales-activities','company-management','contacts','sales'] },
  { type:'group', id:'grp-productions', label:'🏗️ 생산관리', children:['productions','inventory','display'] },
  { type:'item',  key:'ai-assistant' },
  { type:'item',  key:'storage' },
  { type:'item',  key:'mypage' },
  { type:'item',  key:'settings' },
  { type:'item',  key:'backup-restore' },
];
const VALID_GROUP_IDS = new Set(DEFAULT_MENU_LAYOUT.filter(n => n.type === 'group').map(n => n.id));
const GROUP_LABELS = Object.fromEntries(DEFAULT_MENU_LAYOUT.filter(n => n.type === 'group').map(n => [n.id, n.label]));

// 저장된(또는 없으면 기본) 레이아웃을 "지금 이 사용자가 실제로 읽기 권한이 있는 메뉴" 기준으로
// 정리한다 — 권한이 없어진 메뉴는 빼고, 저장 당시에는 없었지만 새로 권한이 생긴 메뉴는 맨 뒤에
// 붙여준다. 빈 그룹은 여기서 지우지 않는다(편집모드에서 드롭 대상으로 계속 보여줘야 하므로) —
// 화면에 실제로 보여줄 때(0/1/2+ 규칙)만 따로 걸러낸다.
function reconcileMenuLayout(saved, allowedKeys) {
  const base = Array.isArray(saved) && saved.length ? saved : DEFAULT_MENU_LAYOUT;
  const seen = new Set();
  const out = [];
  base.forEach(node => {
    if (node.type === 'group') {
      // 2026-08: 기본 3개 그룹(grp-tasks 등) 외에도 사용자가 편집모드에서 즉석으로 만든 그룹
      // (임의의 id)이 저장되어 있을 수 있으므로 id를 더 이상 고정 3개로 제한하지 않는다.
      // 대신 형태만 최소한으로 검증한다(id/children 존재 여부).
      if (typeof node.id !== 'string' || !Array.isArray(node.children)) return;
      const children = node.children.filter(k => allowedKeys.has(k) && MENU_ITEM_META[k]);
      children.forEach(k => seen.add(k));
      // 라벨은 사용자가 저장한 값(node.label)을 최우선으로 쓰고, 없을 때만 기본 3개 그룹의
      // 기본 라벨(GROUP_LABELS)이나 "새 그룹"으로 대체한다 — 사용자가 지은 그룹명이
      // 여기서 덮어써지지 않도록 하기 위함.
      out.push({ type:'group', id: node.id, label: node.label || GROUP_LABELS[node.id] || '새 그룹', children });
    } else if (node.type === 'item' && allowedKeys.has(node.key) && MENU_ITEM_META[node.key]) {
      seen.add(node.key);
      out.push({ type:'item', key: node.key });
    }
  });
  allowedKeys.forEach(k => {
    if (!seen.has(k) && MENU_ITEM_META[k]) out.push({ type:'item', key: k });
  });
  return out;
}

// 편집모드에서 메뉴 항목 하나를 다른 메뉴 항목 위에 "겹쳐서"(드롭 영역 가운데) 놓으면 호출된다.
// 두 항목 모두 최상위(그룹에 속하지 않은) 단일 메뉴여야 하며, 조건을 만족하면 그 자리에
// 새 그룹을 만들어 두 메뉴를 하위로 넣는다. 그룹명은 일단 "새 그룹"으로 두고, 호출부
// (handleDrop)에서 곧바로 이름 입력 상태로 전환해 사용자가 직접 이름을 지을 수 있게 한다.
function mergeIntoNewGroup(layout, fromPath, toPath) {
  if (fromPath.length !== 1 || toPath.length !== 1) return layout;
  const fromTop = fromPath[0], toTop = toPath[0];
  if (fromTop === toTop) return layout;
  const clone = layout.map(n => n.type === 'group' ? { ...n, children: [...n.children] } : { ...n });
  const fromNode = clone[fromTop];
  const toNode = clone[toTop];
  if (!fromNode || !toNode || fromNode.type !== 'item' || toNode.type !== 'item') return layout;
  const hi = Math.max(fromTop, toTop), lo = Math.min(fromTop, toTop);
  clone.splice(hi, 1);
  clone.splice(lo, 1);
  const newGroup = {
    type: 'group',
    id: `grp-custom-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`,
    label: '새 그룹',
    children: [toNode.key, fromNode.key],
  };
  clone.splice(lo, 0, newGroup);
  return clone;
}

// 그룹 이름 변경 — 편집모드에서 그룹 라벨 옆 ✎ 아이콘으로 언제든 다시 부를 수 있다.
function renameGroupLabel(layout, groupId, newLabel) {
  return layout.map(n => (n.type === 'group' && n.id === groupId) ? { ...n, label: newLabel } : n);
}

// 드래그앤드롭 한 번의 결과(레이아웃 트리 안에서 노드 하나를 옮기는 것)를 계산하는 순수 함수.
// path는 [최상위인덱스] 또는(그룹 하위 항목이면) [최상위인덱스, 하위인덱스] 형태다.
function moveMenuNode(layout, fromPath, toPath) {
  const clone = layout.map(n => n.type === 'group' ? { ...n, children: [...n.children] } : { ...n });

  const fromTop = fromPath[0];
  let removed;
  if (fromPath.length === 1) {
    removed = clone.splice(fromTop, 1)[0];
  } else {
    if (!clone[fromTop] || clone[fromTop].type !== 'group') return layout;
    removed = clone[fromTop].children.splice(fromPath[1], 1)[0];
  }
  if (removed === undefined) return layout;

  // 최상위 배열에서 fromTop보다 앞쪽 항목이 사라졌으면, toPath가 가리키던 최상위 인덱스도
  // 하나씩 당겨졌다는 뜻이라 보정해준다.
  let toTop = toPath[0];
  if (fromPath.length === 1 && fromTop < toTop) toTop -= 1;

  if (toPath.length === 1) {
    const itemNode = typeof removed === 'string' ? { type:'item', key: removed } : removed;
    const toIdx = Math.min(Math.max(toTop, 0), clone.length);
    clone.splice(toIdx, 0, itemNode);
  } else {
    if (removed && removed.type === 'group') return layout; // 그룹은 다른 그룹 안에 들어갈 수 없음
    const key = typeof removed === 'string' ? removed : removed.key;
    const groupNode = clone[toTop];
    if (!groupNode || groupNode.type !== 'group') return layout;
    let toChildIdx = toPath[1];
    if (fromPath.length === 2 && fromPath[0] === toTop && fromPath[1] < toChildIdx) toChildIdx -= 1;
    groupNode.children.splice(Math.min(Math.max(toChildIdx, 0), groupNode.children.length), 0, key);
  }
  return clone;
}

// 계정관리 > 권한설정에서 부여한 메뉴별 읽기 권한에 따라 보이는 메뉴가 달라지는 사이드바
function Sidebar() {
  const { user, logout } = useAuth();
  // 본인의 세부 권한설정(메뉴별 읽기 가능 여부) 조회 — 사이드바에서 숨길 메뉴를 결정하기 위함
  // 최고관리자는 /permissions/me가 모든 메뉴에 대해 can_read:1을 내려주므로 별도 분기가 필요 없습니다.
  // 2026-08: 메뉴편집(사이드바 드래그앤드롭) 기능 자체의 사용 가능 여부는 "홈" 메뉴의
  // 쓰기권한(can_write)으로 통제한다 — 그래서 canRead뿐 아니라 canWrite도 함께 조회해둔다.
  const [myPerms, setMyPerms] = useState({ loaded: false, canRead: {}, canWrite: {}, isLocal: false });
  useEffect(() => {
    if (!user) return;
    API.get('/permissions/me')
      .then(r => {
        const canRead = {};
        const canWrite = {};
        (r.data.permissions || []).forEach(p => { canRead[p.menu_key] = !!p.can_read; canWrite[p.menu_key] = !!p.can_write; });
        setMyPerms({ loaded: true, canRead, canWrite, isLocal: !!r.data.isLocal });
      })
      .catch(() => setMyPerms({ loaded: true, canRead: {}, canWrite: {}, isLocal: false })); // 조회 실패 시 안전하게 전부 숨김
  }, [user]);

  // 환경설정(2026-09) — 상단 로고 문구의 회사 브랜드명/이니셜. 인증 없이도 조회 가능한
  // /company-info/brand-public을 사용(로그인 전 화면인 Login.js와 동일 API를 공유).
  const [brand, setBrand] = useState({ brand_name: 'NINE', brand_initial: 'N', brand_suffix: 'IMS' });
  useEffect(() => {
    API.get('/company-info/brand-public')
      .then(r => setBrand({
        brand_name: r.data.brand_name || 'NINE',
        brand_initial: r.data.brand_initial || 'N',
        brand_suffix: r.data.brand_suffix || 'IMS',
      }))
      .catch(() => {});
  }, []);

  // 사이드바 개인화(드래그앤드롭) — 내가 예전에 저장해둔 메뉴 배치가 있으면 불러온다.
  // 저장한 적이 없으면 customLayout은 null로 남고, 화면에는 DEFAULT_MENU_LAYOUT이 쓰인다.
  const [customLayout, setCustomLayout] = useState(null);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState(null);
  const [dragPath, setDragPath] = useState(null);
  const [overPath, setOverPath] = useState(null);
  // overZone: 지금 드래그 중인 항목이 대상 위 어느 영역에 걸쳐 있는지 — 'before'/'after'는
  // 순서만 바꾸는 일반 재배치, 'merge'는 두 메뉴가 겹쳐서 새 그룹으로 묶이는 영역이다.
  const [overZone, setOverZone] = useState(null);
  // 편집모드에서 그룹 이름을 바꾸는 중인 그룹 id(하나만 동시에 편집 가능) + 입력 중인 임시 텍스트.
  const [renamingGroupId, setRenamingGroupId] = useState(null);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  // 메뉴편집 아이콘 버튼 호버 상태 — 평소엔 아이콘만 보이다가 마우스를 올리면 "메뉴 편집"
  // 글자가 옆으로 펼쳐진다.
  const [editBtnHover, setEditBtnHover] = useState(false);
  useEffect(() => {
    if (!user) return;
    API.get('/permissions/me/layout')
      .then(r => setCustomLayout(Array.isArray(r.data?.layout) ? r.data.layout : null))
      .catch(() => setCustomLayout(null))
      .finally(() => setLayoutLoaded(true));
  }, [user]);

  // 스토리지 파일 업로드 진행률(%) — Storage.js가 업로드 도중 window에 커스텀 이벤트
  // ('storage:upload-progress', detail:{percent}|{percent:null})를 쏴주면 사이드바가 이를 듣고
  // "💾 스토리지" 메뉴 라벨 옆에 표시한다. 사이드바(Layout)는 페이지 이동해도 다시 마운트되지
  // 않으므로, 업로드 중 다른 메뉴로 이동해도 진행률 배지가 계속 보인다.
  const [storageUploadPercent, setStorageUploadPercent] = useState(null);
  useEffect(() => {
    const onProgress = (e) => setStorageUploadPercent(e.detail && typeof e.detail.percent === 'number' ? e.detail.percent : null);
    window.addEventListener('storage:upload-progress', onProgress);
    return () => window.removeEventListener('storage:upload-progress', onProgress);
  }, []);

  // 생산전광판 하위에 표시할 제품군 목록 — 재고관리에 등록된 제품군이 2개 이상일 때만 아래에
  // 체크박스 목록으로 펼쳐 보여준다(1개면 굳이 고를 필요가 없으므로 하위 메뉴를 만들지 않음).
  const [displayGroups, setDisplayGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(readSelectedGroupIds);
  // 사이드바(Layout)는 페이지 이동을 해도 다시 마운트되지 않고 계속 떠 있는 컴포넌트라서,
  // 예전에는 displayGroups를 로그인 시점에 딱 한 번만 불러왔습니다. 그래서 재고관리에서
  // 제품군을 삭제/추가한 뒤에도 사이드바는 그 변화를 전혀 모른 채 예전 목록을 계속 보여줬고
  // (삭제한 제품군이 계속 보이거나, 1개만 남았는데도 하위 메뉴가 안 사라지는 원인이었습니다),
  // 아래처럼 location.pathname을 의존성에 넣어서 "생산전광판" 메뉴로 들어올 때마다 최신 목록을
  // 다시 불러오도록 고칩니다.
  const location = useLocation();
  useEffect(() => {
    if (!myPerms.loaded || !myPerms.canRead['display']) return;
    if (location.pathname !== '/display') return;
    API.get('/display/groups').then(r => {
      const freshGroups = r.data || [];
      setDisplayGroups(freshGroups);
      // 로테이션 선택값(localStorage)에 이미 삭제된 제품군 id가 남아있으면 걸러내서,
      // Display.js가 더 이상 존재하지 않는 제품군을 계속 로테이션 대상으로 삼지 않게 한다.
      const freshIds = new Set(freshGroups.map(g => g.id));
      setSelectedGroupIds(prev => {
        const pruned = prev.filter(id => freshIds.has(id));
        if (pruned.length !== prev.length) {
          try { localStorage.setItem(DISPLAY_GROUPS_KEY, JSON.stringify(pruned)); } catch {}
          window.dispatchEvent(new Event('display:groups-changed'));
        }
        return pruned;
      });
    }).catch(() => setDisplayGroups([]));
  }, [myPerms.loaded, myPerms.canRead, location.pathname]);

  // 업무관리/영업관리/생산관리 그룹의 하위메뉴는 기본적으로 접혀 있다가, 그룹 이름을 클릭하면
  // 펼쳐진다(아코디언 방식이라 한 번에 하나만 펼쳐짐). 페이지가 이동하면 "지금 보고 있는 화면이
  // 속한 그룹"을 다시 계산해서 그 그룹만 펼쳐진 상태로 맞춘다 — 그래서 어떤 그룹의 하위메뉴를
  // 클릭해 그 안의 다른 화면으로 이동해도 펼쳐진 상태가 유지되고, 그 그룹과 무관한 다른 메뉴로
  // 이동했을 때만 자동으로 접힌다. (customLayout이 없으면 기본 배치를 기준으로 판단한다.)
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  useEffect(() => {
    const layoutForLookup = Array.isArray(customLayout) && customLayout.length ? customLayout : DEFAULT_MENU_LAYOUT;
    const activeGroup = layoutForLookup.find(node =>
      node.type === 'group' && (node.children || []).some(k => MENU_ITEM_META[k]?.to === location.pathname)
    );
    setExpandedGroupId(activeGroup ? activeGroup.id : null);
  }, [location.pathname, customLayout]);

  const persistGroupSelection = (ids) => {
    setSelectedGroupIds(ids);
    try { localStorage.setItem(DISPLAY_GROUPS_KEY, JSON.stringify(ids)); } catch {}
    window.dispatchEvent(new Event('display:groups-changed'));
  };
  // 체크박스: 30초 로테이션에 포함할 제품군을 추가/제외한다(여러 개 선택 가능).
  const toggleDisplayGroup = (id) => {
    const next = selectedGroupIds.includes(id) ? selectedGroupIds.filter(x => x !== id) : [...selectedGroupIds, id];
    persistGroupSelection(next);
  };
  // 제품군 이름 클릭: 체크박스로 관리하는 30초 로테이션 선택과는 완전히 별개의 동작이다.
  // "지금 이 제품군 하나만 바로 보고 싶다"는 뜻이므로 체크박스 선택(localStorage에 저장되어
  // 로테이션에 쓰이는 값)은 전혀 건드리지 않고, Display.js에 "지금 이것만 보여줘"라는 이벤트만
  // 쏴준다. 다음 30초 로테이션 주기가 되면 체크박스로 선택해둔 목록 기준으로 다시 돌아간다.
  const viewDisplayGroup = (id) => {
    window.dispatchEvent(new CustomEvent('display:view-group', { detail: { groupId: id } }));
  };

  // 생산전광판 하위 제품군 메뉴는 그 메뉴를 실제로 보고 있을 때만(=현재 경로가 /display일 때만)
  // 펼쳐 보여준다. 다른 화면을 보는 중에는 목록에 안 보이다가 생산전광판 메뉴를 누르면 나타남.
  // (location은 위 displayGroups 재조회 useEffect에서 이미 선언해서 재사용한다.)
  const isDisplayActive = location.pathname === '/display';

  if (!user) return null;

  // 2026-08 메뉴 구성 개편 — 업무관리/영업관리/생산관리는 이제 사이드바 상위 그룹이고, 예전에
  // 그 메뉴였던 화면(tasks/sales-activities/productions)은 각 그룹의 "현황" 하위 메뉴가 되었다
  // (라벨도 업무현황/영업현황/생산현황으로 바뀜). 그룹 자체는 별도 페이지가 없는 순수 시각적
  // 묶음이라 menu_key/권한이 없다 — 실제 접근 제어는 여전히 하위 항목 각각의 canRead로 이뤄진다.
  // 실제 배치(순서/그룹핑)는 파일 위쪽의 DEFAULT_MENU_LAYOUT(기본값) 또는 사용자가 드래그앤드롭
  // 으로 저장한 customLayout에서 가져온다 — 아래는 "지금 이 사용자가 읽기 권한을 가진 메뉴 키"
  // 집합만 계산한다.
  const allowedKeysSet = new Set(Object.keys(MENU_ITEM_META).filter(k => {
    const meta = MENU_ITEM_META[k];
    if (meta.localOnly && !myPerms.isLocal) return false;
    // strictSuperadminOnly 메뉴(백업/복구)는 계정관리 > 권한설정 체계에서 완전히 빠졌으므로
    // myPerms.canRead(권한설정 기반 맵)를 보지 않고 role 문자열을 직접 비교한다 — admin 승격
    // 계정에게도 절대 열어주지 않는다.
    if (meta.strictSuperadminOnly) return user?.role === 'superadmin';
    return !!myPerms.canRead[k];
  }));

  // 2026-08: 메뉴편집(사이드바 드래그앤드롭 재구성) 기능 자체는 "홈" 메뉴 쓰기권한이 있는
  // 사용자만 쓸 수 있다 — 최고관리자는 /permissions/me가 모든 메뉴 can_write:1을 내려주므로
  // 자동으로 포함된다.
  const canEditMenu = myPerms.loaded && !!myPerms.canWrite['home'];

  // 그룹(업무관리/영업관리/생산관리)은 그 안에서 실제로 읽기 권한이 있는 하위 메뉴 개수에 따라
  // 다르게 표시한다 — 0개면 그룹 전체를 숨기고, 1개면 "카테고리를 묶지 말고 상위에 표기"라는
  // 요청대로 그 하나의 메뉴를 그룹 없이 최상위 메뉴처럼 바로 보여주며, 2개 이상이면 그룹 이름
  // 아래 하위 메뉴 목록으로 묶어서 보여준다(사용자가 직접 재구성한 그룹도 동일한 규칙 적용).
  const viewLayout = reconcileMenuLayout(customLayout, allowedKeysSet);
  const menus = viewLayout.flatMap(node => {
    if (node.type === 'item') return [{ key: node.key, ...MENU_ITEM_META[node.key] }];
    if (node.children.length === 0) return [];
    if (node.children.length === 1) {
      const k = node.children[0];
      return [{ key: k, ...MENU_ITEM_META[k] }];
    }
    return [{ groupId: node.id, groupLabel: node.label, children: node.children.map(k => ({ key: k, ...MENU_ITEM_META[k] })) }];
  });

  // ── 편집모드 진입/초기화/드롭 처리 ──────────────────────────────────────────────────
  const handleEnterEditMode = () => {
    setDraftLayout(reconcileMenuLayout(customLayout, allowedKeysSet));
    setRenamingGroupId(null);
    setEditMode(true);
  };
  const persistLayout = (next) => {
    setDraftLayout(next);
    setCustomLayout(next);
    API.put('/permissions/me/layout', { layout: next }).catch(() => {});
  };
  const handleResetLayout = () => {
    setRenamingGroupId(null);
    persistLayout(reconcileMenuLayout(DEFAULT_MENU_LAYOUT, allowedKeysSet));
  };
  // action이 없으면 일반 재배치(순서변경/그룹이동), 'merge'면 두 최상위 메뉴를 겹쳐서 새
  // 그룹으로 묶는다. 그룹이 새로 만들어지면 곧바로 이름 입력 상태로 전환해서, 사용자가
  // "새 그룹"이라는 기본값을 바로 자기가 원하는 이름으로 바꿀 수 있게 한다.
  const handleDrop = (toPath, action) => {
    if (!dragPath) return;
    const from = dragPath;
    setDragPath(null);
    setOverPath(null);
    setOverZone(null);
    if (action === 'merge') {
      const merged = mergeIntoNewGroup(draftLayout, from, toPath);
      if (merged === draftLayout) return;
      persistLayout(merged);
      const newIdx = Math.min(from[0], toPath[0]);
      const newNode = merged[newIdx];
      if (newNode && newNode.type === 'group') {
        setGroupNameDraft(newNode.label);
        setRenamingGroupId(newNode.id);
      }
      return;
    }
    if (JSON.stringify(from) === JSON.stringify(toPath)) return;
    persistLayout(moveMenuNode(draftLayout, from, toPath));
  };

  // 메뉴 한 줄(단일 항목) 렌더링 — 그룹 하위 항목이든 최상위 항목이든 동일하게 쓴다(들여쓰기만
  // indentPx로 다르게). 생산전광판(display) 전용 부가 로직(제품군 로테이션 체크박스, 재클릭시
  // 새로고침 이벤트)과 스토리지 업로드 % 배지는 그룹 안에 들어가 있든 최상위에 단독으로
  // flatten되어 있든 항상 동일하게 동작해야 하므로 이 공용 함수 안에서 처리한다.
  const renderMenuItem = (m, indentPx) => (
    <div key={m.to}>
      <Link to={m.to} style={{ display:'block', padding:`10px 20px 10px ${indentPx}px`, color:'#cbd5e1', fontSize:14, textDecoration:'none' }}
        onMouseEnter={e => e.target.style.background='#334155'}
        onMouseLeave={e => e.target.style.background='transparent'}
        onClick={m.key === 'display' ? () => window.dispatchEvent(new Event('display:refresh')) : undefined}>
        {m.label}
        {m.key === 'storage' && storageUploadPercent !== null && (
          <span style={{ marginLeft:6, fontSize:12, fontWeight:700, color:'#38bdf8' }}>
            ({storageUploadPercent}%)
          </span>
        )}
      </Link>

      {m.key === 'display' && displayGroups.length > 1 && isDisplayActive && (
        <div style={{ padding:'2px 0 6px' }}>
          {displayGroups.map(g => (
            <div key={g.id}
              style={{ display:'flex', alignItems:'center', gap:8, padding:`6px 20px 6px ${indentPx + 14}px` }}>
              <input type="checkbox" checked={selectedGroupIds.includes(g.id)}
                onChange={() => toggleDisplayGroup(g.id)}
                style={{ cursor:'pointer', flexShrink:0 }} />
              <span onClick={() => viewDisplayGroup(g.id)}
                style={{ color:'#94a3b8', fontSize:13, cursor:'pointer', flex:1 }}
                onMouseEnter={e => e.target.style.color='#e2e8f0'}
                onMouseLeave={e => e.target.style.color='#94a3b8'}>
                {g.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // 업무관리/영업관리/생산관리처럼 하위 메뉴가 2개 이상 남은 그룹 — 그룹 이름은 기본적으로
  // 접혀 있고, 클릭하면 펼쳐진다(한 번에 하나만 펼쳐지는 아코디언 방식). 다른 메뉴를 눌러
  // 페이지가 바뀌면 위쪽 useEffect(location.pathname 감시)가 자동으로 다시 접어준다.
  const renderMenuGroup = (m) => {
    const isExpanded = expandedGroupId === m.groupId;
    return (
      <div key={m.groupLabel}>
        <div onClick={() => setExpandedGroupId(isExpanded ? null : m.groupId)}
          style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'10px 20px', color:'#cbd5e1', fontSize:14, cursor:'pointer', userSelect:'none' }}
          onMouseEnter={e => e.currentTarget.style.background='#334155'}
          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
          <span>{m.groupLabel}</span>
          <span style={{ fontSize:11, color:'#94a3b8' }}>{isExpanded ? '▾' : '▸'}</span>
        </div>
        {isExpanded && m.children.map(c => renderMenuItem(c, 34))}
      </div>
    );
  };

  // ── 편집모드 전용 렌더링 (드래그앤드롭) ──────────────────────────────────────────────
  // 편집 중에는 실제 이동(Link)이 아니라 배치 조정만 하는 화면이라, 클릭해도 페이지로
  // 이동하지 않는 일반 div로 그린다. path는 [최상위인덱스] 또는 [최상위인덱스, 하위인덱스].
  const renderEditableItem = (key, path) => {
    const meta = MENU_ITEM_META[key];
    if (!meta) return null;
    const pathKey = path.join('-');
    const isOver = overPath === pathKey;
    const zone = isOver ? overZone : null;
    return (
      <div key={pathKey}
        draggable
        onDragStart={e => { e.stopPropagation(); setDragPath(path); e.dataTransfer.effectAllowed = 'move'; }}
        onDragEnd={() => { setDragPath(null); setOverPath(null); setOverZone(null); }}
        onDragOver={e => {
          e.preventDefault(); e.stopPropagation();
          // 최상위 항목끼리(그룹에 안 속한 단일 메뉴)만 "겹쳐서 그룹 만들기"가 가능하다 —
          // 가운데(높이의 30~70%) 영역에 놓으면 merge, 위/아래 가장자리는 순서변경(before/after).
          const rect = e.currentTarget.getBoundingClientRect();
          const relY = e.clientY - rect.top;
          let nextZone;
          if (path.length === 1) {
            nextZone = relY < rect.height * 0.3 ? 'before' : relY > rect.height * 0.7 ? 'after' : 'merge';
          } else {
            nextZone = relY < rect.height / 2 ? 'before' : 'after';
          }
          setOverPath(pathKey);
          setOverZone(nextZone);
        }}
        onDrop={e => {
          e.preventDefault(); e.stopPropagation();
          if (path.length === 1 && overZone === 'merge') {
            handleDrop(path, 'merge');
            return;
          }
          const toPath = overZone === 'after'
            ? (path.length === 1 ? [path[0] + 1] : [path[0], path[1] + 1])
            : path;
          handleDrop(toPath);
        }}
        style={{ display:'flex', alignItems:'center', gap:8, margin:'2px 8px',
          padding:`8px 12px 8px ${path.length === 2 ? 26 : 12}px`,
          background: zone === 'merge' ? '#1e3a5f' : (isOver ? '#334155' : 'transparent'),
          borderTop: zone === 'before' ? '2px solid #38bdf8' : '2px solid transparent',
          borderBottom: zone === 'after' ? '2px solid #38bdf8' : '2px solid transparent',
          outline: zone === 'merge' ? '2px dashed #38bdf8' : 'none', outlineOffset: -2,
          cursor:'grab', borderRadius:4 }}>
        <span style={{ color:'#475569', fontSize:13 }}>⠿</span>
        <span style={{ color:'#cbd5e1', fontSize:14 }}>{meta.label}</span>
        {zone === 'merge' && <span style={{ marginLeft:'auto', fontSize:11, color:'#38bdf8' }}>🔗 그룹으로 묶기</span>}
      </div>
    );
  };
  const renderEditableGroup = (node, topIdx) => {
    const headerPathKey = `${topIdx}`;
    const isOver = overPath === headerPathKey;
    const isRenaming = renamingGroupId === node.id;
    const commitRename = () => {
      const label = groupNameDraft.trim() || node.label;
      persistLayout(renameGroupLabel(draftLayout, node.id, label));
      setRenamingGroupId(null);
    };
    return (
      <div key={node.id}>
        <div
          draggable={!isRenaming}
          onDragStart={e => { e.stopPropagation(); setDragPath([topIdx]); e.dataTransfer.effectAllowed = 'move'; }}
          onDragEnd={() => { setDragPath(null); setOverPath(null); setOverZone(null); }}
          onDragOver={e => { e.preventDefault(); e.stopPropagation(); setOverPath(headerPathKey); setOverZone('merge'); }}
          onDrop={e => {
            e.preventDefault(); e.stopPropagation();
            if (!dragPath) return;
            // 드래그 중인 게 그룹 자신이면 최상위 재정렬, 개별 메뉴 항목이면 이 그룹 안으로 추가
            // (이미 그룹이 있는 곳에 드롭하는 것이므로 새 그룹을 또 만들지 않고 여기 합류시킨다).
            const draggedIsGroup = dragPath.length === 1 && draftLayout[dragPath[0]] && draftLayout[dragPath[0]].type === 'group';
            handleDrop(draggedIsGroup ? [topIdx] : [topIdx, node.children.length]);
          }}
          style={{ margin:'6px 8px 0', padding:'8px 12px', color:'#64748b', fontSize:12, fontWeight:700,
            letterSpacing:0.3, background: isOver ? '#334155' : 'transparent', cursor: isRenaming ? 'default' : 'grab', borderRadius:4,
            display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:13 }}>⠿</span>
          {isRenaming ? (
            <input
              autoFocus
              draggable={false}
              value={groupNameDraft}
              onChange={e => setGroupNameDraft(e.target.value)}
              onMouseDown={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setRenamingGroupId(null);
              }}
              onBlur={commitRename}
              style={{ flex:1, background:'#0f172a', border:'1px solid #475569', borderRadius:4,
                color:'#e2e8f0', fontSize:12, fontWeight:700, padding:'2px 6px', minWidth:0 }}
            />
          ) : (
            <span style={{ flex:1 }}>{node.label}</span>
          )}
          {node.children.length === 0 && !isRenaming && <span style={{ color:'#475569', fontWeight:400, fontSize:11 }}>(비어있음 · 여기로 드래그)</span>}
          {!isRenaming && (
            <span
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); setGroupNameDraft(node.label); setRenamingGroupId(node.id); }}
              title="그룹 이름 바꾸기"
              style={{ fontSize:12, cursor:'pointer', color:'#94a3b8', flexShrink:0 }}>
              ✎
            </span>
          )}
        </div>
        {node.children.map((key, childIdx) => renderEditableItem(key, [topIdx, childIdx]))}
      </div>
    );
  };

  return (
    <div style={{ width:200, background:'#1e293b', height:'100vh', padding:'20px 0', flexShrink:0,
      position:'sticky', top:0, overflowY:'auto' }}>
      <div style={{ padding:'0 20px 20px', borderBottom:'1px solid #334155', textAlign:'center', position:'relative' }}>
        {/* 2026-08 요청: 홈 사이드바 메뉴를 없애는 대신, 이 로고를 누르면 홈("/")으로 이동한다. */}
        <Link to="/" style={{ color:'#fff', fontWeight:700, fontSize:24, textDecoration:'none', display:'block' }}>
          {brand.brand_name} {brand.brand_suffix}
        </Link>
        <div style={{ color:'#64748b', fontSize:12, marginTop:12, lineHeight:1.4 }}>
          Integrated<br/>Management System
        </div>
        {/* 1줄: 직급 + 이름, 2줄: (부서) — 예전에는 2줄에 (최고관리자/협력사/사내임직원) 같은
            회원구분을 보여줬지만, 요청에 따라 부서를 보여주도록 변경.
            단, 진짜 최고관리자(role==='superadmin') 계정은 2026-08 요청에 따라
            직급/이름/부서 대신 "최고관리자 (아이디)" 로 고정 표시한다. 부여받은 관리자
            (role==='admin')는 대상이 아니라 기존처럼 직급/이름/부서를 그대로 보여준다. */}
        {user.role === 'superadmin' ? (
          <>
            <div style={{ color:'#94a3b8', fontSize:14, marginTop:6 }}>
              최고관리자
            </div>
            <div style={{ color:'#64748b', fontSize:12, marginTop:2 }}>
              ({user.username})
            </div>
          </>
        ) : (
          <>
            <div style={{ color:'#94a3b8', fontSize:14, marginTop:6 }}>
              {[user.position, user.name].filter(Boolean).join(' ')}
            </div>
            {user.department && (
              <div style={{ color:'#64748b', fontSize:12, marginTop:2 }}>
                ({user.department})
              </div>
            )}
          </>
        )}

        {/* 2026-08: 메뉴편집 진입 버튼 — 헤더 우측 하단에 작은 원형 아이콘으로 표시하고,
            마우스를 올렸을 때만 "메뉴 편집" 글자가 옆으로 펼쳐진다. 편집모드 중에는 숨기고
            (그 자리 대신 draftLayout 하단에 초기화/완료 버튼이 뜬다), 권한설정에서 "홈" 메뉴
            쓰기권한(canEditMenu)이 있는 사용자에게만 보여준다. */}
        {layoutLoaded && canEditMenu && !editMode && (
          <button onClick={handleEnterEditMode}
            onMouseEnter={() => setEditBtnHover(true)}
            onMouseLeave={() => setEditBtnHover(false)}
            title="메뉴 편집"
            style={{
              position:'absolute', bottom:6, left:8,
              display:'flex', alignItems:'center', justifyContent: editBtnHover ? 'flex-start' : 'center',
              gap:6, height:26, width: editBtnHover ? 104 : 26,
              padding: editBtnHover ? '0 10px' : 0,
              background:'#334155', border:'1px solid #475569', borderRadius:13,
              color:'#e2e8f0', fontSize:12, fontWeight:600, cursor:'pointer',
              overflow:'hidden', whiteSpace:'nowrap', transition:'width 0.18s ease, padding 0.18s ease',
            }}>
            <span style={{ fontSize:13, flexShrink:0 }}>✏️</span>
            {editBtnHover && <span>메뉴 편집</span>}
          </button>
        )}
      </div>

      {editMode ? (
        <div style={{ marginTop:8 }}>
          <div style={{ padding:'0 20px 8px', color:'#64748b', fontSize:11, lineHeight:1.4 }}>
            메뉴를 드래그해서 순서를 바꾸거나 다른 그룹으로 옮길 수 있어요. 메뉴 두 개를 서로
            겹쳐서 놓으면 새 그룹으로 묶이고, 그룹명 옆 ✎ 아이콘으로 이름을 바꿀 수 있어요.
          </div>
          {draftLayout.map((node, topIdx) => (
            node.type === 'group'
              ? renderEditableGroup(node, topIdx)
              : renderEditableItem(node.key, [topIdx])
          ))}
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleDrop([draftLayout.length]); }}
            style={{ height:20 }} />
          <div style={{ display:'flex', gap:8, padding:'8px 20px 4px' }}>
            <button onClick={handleResetLayout}
              style={{ flex:1, padding:'8px 0', background:'#334155', border:'none', borderRadius:6,
                color:'#cbd5e1', fontSize:13, cursor:'pointer' }}>
              ↺ 초기화
            </button>
            <button onClick={() => { setRenamingGroupId(null); setEditMode(false); }}
              style={{ flex:1, padding:'8px 0', background:'#2563eb', border:'none', borderRadius:6,
                color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              ✅ 완료
            </button>
          </div>
        </div>
      ) : (
        <nav style={{ marginTop:4 }}>
          {menus.map(m => m.children ? renderMenuGroup(m) : renderMenuItem(m, 20))}
          <button onClick={logout}
            style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 20px',
              background:'none', border:'none', color:'#cbd5e1', fontSize:14, cursor:'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background='#334155'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            🚪 로그아웃
          </button>
        </nav>
      )}
    </div>
  );
}

function Layout() {
  const { user, authLoading } = useAuth();
  const [hideSidebar, setHideSidebar] = useState(false);
  const location = useLocation();
  // 상태바(최신글/업데이트 표시)는 생산전광판("/display")을 제외한 모든 페이지(홈 포함)에서
  // 보인다 — 생산전광판은 매장/현장에 그대로 띄워두는 화면이라 다른 UI가 겹치면 안 되기 때문.
  const showActivityFeed = location.pathname !== '/display';

  // 전체화면 상태 감지 → 사이드바 자동 숨김/표시
  useEffect(() => {
    const handler = () => {
      setHideSidebar(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // 다른 탭에 세션이 있는지 짧게 확인하는 중 — PrivateRoute와 동일한 이유로 대기
  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;

  // 생산현황판 전용 계정(accountType === 'display_only')은 좌측 메뉴 자체가 필요 없다 —
  // 로그인하면 항상 생산전광판만 보여준다. Login.js가 로그인/세션복구 시점에 이미 "/display"로
  // 보내주지만, 그 화면에서 다른 경로로 직접 이동(URL 변경 등)하는 것까지 막기 위해 여기서도
  // 한 번 더 강제한다. 로그아웃은 사이드바 대신 Display.js 우측 하단 버튼으로 대신한다.
  const isDisplayOnlyAccount = user.accountType === 'display_only';
  if (isDisplayOnlyAccount && location.pathname !== '/display') {
    return <Navigate to="/display" replace />;
  }

  return (
    <div style={{ display:'flex' }}>
      {/* 전체화면 시, 그리고 생산현황판 전용 계정은 항상 사이드바 숨김 */}
      {!hideSidebar && !isDisplayOnlyAccount && <Sidebar />}
      {/* 요청사항: "하단 상태표시줄과 사내 메신저 때문에 화면이 가려짐" — 사이드바 쪽이 아니라
          우측 콘텐츠 영역에만 하단 여백 60px를 둔다. Routes 뒤에 별도 spacer div를 추가하는
          방식은 콘텐츠가 짧을 때 100vh를 넘겨 스크롤을 만들고, 그 여백이 사이드바(고정 높이
          100vh) 아래쪽 사이드바 없는 빈 줄처럼 보이는 문제가 있었다. 대신 이 컨테이너 자체에
          padding-bottom + box-sizing:border-box를 줘서, 스크롤을 늘리지 않고 항상 우측 콘텐츠
          내부 하단에만 60px가 확보되도록 한다(생산전광판 "/display"는 제외). */}
      <div style={{
        flex:1, background:'#f8fafc', minHeight:'100vh', minWidth:0,
        boxSizing:'border-box', paddingBottom: showActivityFeed ? 60 : 0,
      }}>
        <Routes>
          <Route path="/" element={<PrivateRoute menuKey="home"><Home /></PrivateRoute>} />
          <Route path="/admin/users"
            element={<PrivateRoute superadminOnly><AdminUsers /></PrivateRoute>} />
          <Route path="/tasks"
            element={<PrivateRoute menuKey="tasks"><Tasks /></PrivateRoute>} />
          <Route path="/sales"
            element={<PrivateRoute menuKey="sales"><Sales /></PrivateRoute>} />
          <Route path="/sales-activities"
            element={<PrivateRoute menuKey="sales-activities"><SalesActivities /></PrivateRoute>} />
          <Route path="/certs"
            element={<PrivateRoute menuKey="certs"><Certs /></PrivateRoute>} />
          <Route path="/contracts"
            element={<PrivateRoute menuKey="contracts"><Contracts /></PrivateRoute>} />
          <Route path="/schedules"
            element={<PrivateRoute menuKey="schedules"><Schedules /></PrivateRoute>} />
          <Route path="/sticky-notes"
            element={<PrivateRoute menuKey="sticky-notes"><StickyNotes /></PrivateRoute>} />
          <Route path="/mindmap"
            element={<PrivateRoute menuKey="mindmap"><MindMap /></PrivateRoute>} />
          <Route path="/hr-management"
            element={<PrivateRoute menuKey="hr-management"><HRManagement /></PrivateRoute>} />
          <Route path="/assets"
            element={<PrivateRoute menuKey="assets"><Assets /></PrivateRoute>} />
          <Route path="/accounting"
            element={<PrivateRoute menuKey="accounting"><Accounting /></PrivateRoute>} />
          <Route path="/e-approval"
            element={<PrivateRoute menuKey="e-approval"><ElectronicApproval /></PrivateRoute>} />
          <Route path="/email"
            element={<PrivateRoute menuKey="email"><Email /></PrivateRoute>} />
          <Route path="/storage"
            element={<PrivateRoute menuKey="storage"><Storage /></PrivateRoute>} />
          <Route path="/productions" element={<PrivateRoute menuKey="productions"><Productions /></PrivateRoute>} />
          <Route path="/inventory"   element={<PrivateRoute menuKey="inventory"><Inventory /></PrivateRoute>} />
          <Route path="/notices"     element={<PrivateRoute menuKey="notices"><Notices /></PrivateRoute>} />
          <Route path="/display"     element={<PrivateRoute menuKey="display"><Display /></PrivateRoute>} />
          <Route path="/mypage"      element={<PrivateRoute menuKey="mypage"><MyPage /></PrivateRoute>} />
          <Route path="/settings"    element={<PrivateRoute menuKey="settings"><Settings /></PrivateRoute>} />
          <Route path="/company-management" element={<PrivateRoute menuKey="company-management"><Companies /></PrivateRoute>} />
          <Route path="/contacts" element={<PrivateRoute menuKey="contacts"><Contacts /></PrivateRoute>} />
          <Route path="/ai-assistant" element={<PrivateRoute menuKey="ai-assistant"><AiAssistant /></PrivateRoute>} />
          <Route path="/backup-restore"
            element={<PrivateRoute strictSuperadminOnly localOnly><BackupRestore /></PrivateRoute>} />
          <Route path="*" element={<div style={{padding:40}}>🚧 준비 중인 메뉴입니다.</div>} />
        </Routes>
      </div>
      {showActivityFeed && <ActivityFeedBar />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* 사내 메신저는 더 이상 별도 창(window.open)으로 뜨지 않고 항상 이 페이지 안에서
              떠있는 창(ChatMainWindow)으로 해결되므로, 예전에 있던 /chat 라우트는 제거했습니다. */}
          <Route path="/*" element={
            <ChatProvider>
              {/* 3시간 미사용 자동 로그아웃(생산전광판 실행 중이면 제외) — Layout 안이 아니라
                  여기 바깥에 둬서, 화면(라우트)이 바뀌어도 다시 마운트되지 않고 활동 감지가
                  끊김없이 계속 이어지게 한다. */}
              <AutoLogout />
              <TaskRequestAlert />
              <ScheduleNotifyAlert />
              <HrInfoRequestAlert />
              <StorageBackupReminderAlert />
              <StorageShareAlert />
              <IncomeTaxTableReminderAlert />
              <LaborContractSignAlert />
              <Layout />
              <ChatMainWindow />
              <ChatSidebarMenu />
            </ChatProvider>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}